import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/v2/button";
import { SortableTable } from "@/components/ui/v2/sortable-table";
import { Switch } from "@/components/ui/v2/switch";
import { useDescriptionGroup } from "@/lib/contexts/DescriptionGroup/useDescriptionGroup";
import { usePrompt } from "@/lib/hooks/usePrompt";
import useToast from "@/lib/hooks/useToast";
import { DescriptionGroup } from "@/types/request/UpdateDescriptionGroupsRequest";

type DescriptionGroupsFormProps = {
  clientId: string;
  tenantId: string | null;
};

const DescriptionGroupsForm = ({
  tenantId,
}: DescriptionGroupsFormProps) => {
  const { t } = useTranslation();
  const { descriptionGroups, saveDescriptionGroups } = useDescriptionGroup();
  const { showToast } = useToast();

  const [localOrder, setLocalOrder] = useState<DescriptionGroup[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (descriptionGroups && !isDirty) {
      setLocalOrder(
        descriptionGroups.map((g) => ({
          descriptions: g.descriptions,
          isEnabled: g.isEnabled,
          name: g.name,
          priority: g.priority,
        }))
      );
    }
  }, [descriptionGroups, isDirty]);

  const { markSaved, confirmDialog } = usePrompt(
    {
      title: t("prompts.unsavedChanges.title"),
      description: t("prompts.unsavedChanges.description"),
    },
    isDirty
  );

  const handleReorder = useCallback((newOrder: DescriptionGroup[]) => {
    setLocalOrder(
      newOrder.map((item, index) => ({
        ...item,
        priority: index + 1,
      }))
    );
    setIsDirty(true);
  }, []);

  const handleSave = () => {
    saveDescriptionGroups.mutate(
      { descriptionGroups: localOrder, tenantId },
      {
        onSuccess: () => {
          showToast(t("common.success"));
          setIsDirty(false);
          markSaved();
        },
        onError: () => {
          showToast(t("common.error"), "error");
        },
      }
    );
  };

  const handleCancel = () => {
    if (descriptionGroups) {
      setLocalOrder(
        descriptionGroups.map((g) => ({
          descriptions: g.descriptions,
          isEnabled: g.isEnabled,
          name: g.name,
          priority: g.priority,
        }))
      );
    }
    setIsDirty(false);
  };

  const handleToggleChange = (
    name: DescriptionGroup["name"],
    checked: boolean
  ) => {
    setLocalOrder((prev) =>
      prev.map((g) => (g.name === name ? { ...g, isEnabled: checked } : g))
    );
    setIsDirty(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-foreground">
        {`${t("common.channel")} ${t("common.priority")}`}
      </h3>
      <SortableTable<DescriptionGroup>
        columns={[
          {
            header: "Publication Channel",
            accessor: "name",
            width: "90%",
          },
          {
            header: "Enabled",
            accessor: "isEnabled",
            width: "10%",
            Cell: (row) => (
              <Switch
                disabled={descriptionGroups?.find((g) => g.name === row.name)?.isDefault}
                checked={row.isEnabled}
                onCheckedChange={(checked) =>
                  handleToggleChange(row.name, checked)
                }
              />
            ),
          },
        ]}
        data={localOrder}
        dragColumnLabel="Prio"
        itemKey="name"
        onReorder={handleReorder}
      />
      {isDirty && (
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" type="button" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saveDescriptionGroups.isPending}
          >
            {saveDescriptionGroups.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {t("common.save")}
          </Button>
        </div>
      )}
      {confirmDialog}
    </div>
  );
};

export default DescriptionGroupsForm;
